// Persistent WebSocket client — SPEC §10.
// Connects with JWT token, auto-reconnects with exponential backoff, replays
// missed events on reconnect via { type: "resync", since_event_id }.
// Handler is stored in a ref so the WS connection is not torn down on re-renders.
//
// Carer-parity S2 / D8a: bearer source migrated from direct getAccessToken
// import to useBearer().getBearer(). Hook is now context-aware — when mounted
// inside the carer subtree, connects with the carer bearer; when mounted
// inside the coordinator subtree, connects with the coordinator bearer.
// Enables carer-side drawer at S3b without backend changes (the WS endpoint
// already accepts both JWT shapes).
//
// Carer-parity S3b / Sub-9 = 9a: connection effect deps include `getBearer`
// so a subtree switch (carer ↔ coordinator) tears down the existing socket
// and reopens with the new bearer. `useBearer` memoises `getBearer` via
// useCallback on `[storage, storageKey]`, so within a stable subtree the
// identity is constant and the effect doesn't churn on every render — it
// only re-runs when the surrounding BearerProvider's backend or storageKey
// actually changes. Within-subtree token rotation (silent refresh on 401)
// continues to flow through the close-handler reconnect path: the
// rotated-out token expires, the server closes the socket, the client
// reads the new token from `getBearerRef.current()` and reconnects.

import { useEffect, useRef } from "react";
import { useBearer } from "./useBearer.js";

export interface WsEvent {
  event_type: string;
  tenant_id:  string;
  payload:    Record<string, unknown>;
  ts:         string;
  event_id?:  string;
}

type EventHandler = (event: WsEvent) => void;

const WS_PATH      = "/hcm/api/v1/ws";
const MAX_BACKOFF  = 30_000;

function buildWsUrl(token: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${WS_PATH}?token=${encodeURIComponent(token)}`;
}

interface ConnectionRefs {
  handlerRef:    React.RefObject<EventHandler | null>;
  getBearerRef:  React.RefObject<() => string | null>;
  wsRef:         React.MutableRefObject<WebSocket | null>;
  backoffRef:    React.MutableRefObject<number>;
  lastEventRef:  React.MutableRefObject<string | null>;
  activeRef:     React.MutableRefObject<boolean>;
}

function attachListeners(ws: WebSocket, refs: ConnectionRefs, reconnect: () => void): void {
  ws.addEventListener("open", () => {
    refs.backoffRef.current = 1_000;
    ws.send(JSON.stringify({ type: "resync", since_event_id: refs.lastEventRef.current }));
  });
  ws.addEventListener("message", (ev: MessageEvent<string>) => {
    try {
      const data = JSON.parse(ev.data) as WsEvent;
      if (data.event_id) refs.lastEventRef.current = data.event_id;
      refs.handlerRef.current?.(data);
    } catch { /* ignore malformed frames */ }
  });
  ws.addEventListener("close", (ev: CloseEvent) => {
    refs.wsRef.current = null;
    if (!refs.activeRef.current) return;
    // 1008 = auth failure (missing/expired token) — stop reconnecting to avoid a tight loop.
    if (ev.code === 1008) return;
    const delay = refs.backoffRef.current;
    refs.backoffRef.current = Math.min(delay * 2, MAX_BACKOFF);
    setTimeout(reconnect, delay);
  });
}

function startConnection(refs: ConnectionRefs): void {
  const token = refs.getBearerRef.current?.();
  if (!token || !refs.activeRef.current) return;
  const ws = new WebSocket(buildWsUrl(token));
  refs.wsRef.current = ws;
  attachListeners(ws, refs, () => startConnection(refs));
}

export function useWebSocket(onEvent: EventHandler | null): void {
  const handlerRef   = useRef<EventHandler | null>(onEvent);
  handlerRef.current = onEvent;
  const { getBearer } = useBearer();
  const getBearerRef = useRef(getBearer);
  getBearerRef.current = getBearer;
  const wsRef        = useRef<WebSocket | null>(null);
  const backoffRef   = useRef(1_000);
  const lastEventRef = useRef<string | null>(null);
  const activeRef    = useRef(false);

  useEffect(() => {
    activeRef.current = true;
    const refs: ConnectionRefs = { handlerRef, getBearerRef, wsRef, backoffRef, lastEventRef, activeRef };
    startConnection(refs);
    return (): void => {
      activeRef.current = false;
      wsRef.current?.close();
    };
  }, [getBearer]); // Sub-9 9a: subtree switch reopens with the new bearer; within-subtree refreshes flow through the close-on-1008 reconnect path.
}
